import { useState, useEffect } from 'react';
import Select from '@blaze-react/select';
import { useToasts } from '@blaze-react/toaster';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  pushToDataLayer,
  submitSalesforceLead,
  checkValidEmail,
  RESIDENCE_COUNTRIES
} from '../../utils';
import { PRIVACY } from '../../constants';

const countryOfResidenceOptions = [
  ['', '* What is your country of residence?'],
  ...RESIDENCE_COUNTRIES.map(country => [country, country])
];

const BoatProForm = ({ embed, leadSource, successMessage }) => {
  const [successMessageVisible, setSuccessMessageVisible] = useState(false);
  const [submitState, setSubmitState] = useState(null);

  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [website, setWebsite] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState(null);
  const [countryOfResidence, setCountryOfResidence] = useState('');
  const [additionalTermsAccepted, setAdditionalTermsAccepted] = useState(null);

  const [companyError, setCompanyError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastNameError, setLastNameError] = useState(null);
  const [websiteError, setWebsiteError] = useState(null);
  const [termsAcceptedError, setTermsAcceptedError] = useState(null);
  const [countryOfResidenceError, setCountryOfResidenceError] = useState(null);
  const [additionalTermsAcceptedError, setAdditionalTermsAcceptedError] = useState(null);

  const { addToast } = useToasts();

  const fieldsWrapperClass = `boat-pro-form__fields-wrapper boat-pro-form__fields-wrapper--embed-${embed}`;
  const inputClass = 'boat-pro-form__input';
  const inputWrapperClass = 'boat-pro-form__input-wrapper';
  const doubleInputWrapperClass = 'boat-pro-form__double-input-wrapper';
  const errorClass = 'boat-pro-form__error';

  const companyInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!companyError,
    [inputClass]: !companyError
  });

  const emailInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!emailError,
    [inputClass]: !emailError
  });

  const firstNameInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!firstNameError,
    [inputClass]: !firstNameError
  });

  const lastNameInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!lastNameError,
    [inputClass]: !lastNameError
  });

  const websiteInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!websiteError,
    [inputClass]: !websiteError
  });

  const termsAcceptedInputClass = classnames({
    'boat-pro-form__checkbox boat-pro-form__checkbox--error': !!termsAcceptedError,
    'boat-pro-form__checkbox': !termsAcceptedError
  });

  const additionalTermsAcceptedInputClass = classnames({
    'boat-pro-form__radio boat-pro-form__radio--error': !!additionalTermsAcceptedError,
    'boat-pro-form__radio': !additionalTermsAcceptedError
  });

  const countryOfResidenceInputClass = classnames({
    [`${inputClass} ${inputClass}--error`]: !!countryOfResidenceError,
    [inputClass]: !countryOfResidenceError
  });

  const handleKeyDown = event => {
    if (event.keyCode !== 13) return;

    setSubmitState({ target: event.target });
  };

  const submit = async (
    passedEmail,
    passedLeadSource,
    passedFirstName,
    passedLastName,
    passedCompany,
    passedWebsite,
    passedCountryOfResidence,
    passedPhoneNumber,
    passedAdditionalTermsAccepted
  ) => {
    try {
      await submitSalesforceLead(
        passedEmail,
        passedLeadSource,
        passedFirstName,
        passedLastName,
        passedCompany,
        passedWebsite,
        passedCountryOfResidence,
        passedPhoneNumber,
        null,
        null,
        null,
        passedAdditionalTermsAccepted
      );

      setSuccessMessageVisible(true);

      pushToDataLayer({ event: 'Lead Generated', leadSource: passedLeadSource });

      return true;
    } catch {
      addToast('Error while submitting the form, please try again', {
        appearance: 'error',
        autoDismiss: true
      });

      return false;
    }
  };

  useEffect(
    () => {
      if (submitState === null) return;

      let valid = true;
      if (!company) {
        setCompanyError('Required field');
        valid = false;
      }
      if (!email) {
        setEmailError('Required field');
        valid = false;
      } else if (!checkValidEmail(email)) {
        setEmailError('Invalid email format');
        valid = false;
      }
      if (!firstName) {
        setFirstNameError('Required field');
        valid = false;
      }
      if (!lastName) {
        setLastNameError('Required field');
        valid = false;
      }
      if (!website) {
        setWebsiteError('Required field');
        valid = false;
      }
      if (!termsAccepted) {
        setTermsAcceptedError('Please accept the terms and conditions');
        valid = false;
      }
      if (!RESIDENCE_COUNTRIES.includes(countryOfResidence)) {
        setCountryOfResidenceError('Required field');
        valid = false;
      }
      if (additionalTermsAccepted === null) {
        setAdditionalTermsAcceptedError('Please select YES or NO on the additional terms');
        valid = false;
      }
      if (!valid) {
        setSubmitState(null);
        return;
      }

      if (submitState.target) {
        submitState.target.blur();
      }

      (async () => {
        const successful = await submit(
          email,
          leadSource,
          firstName,
          lastName,
          company,
          website,
          countryOfResidence,
          phoneNumber,
          additionalTermsAccepted
        );

        setSubmitState(null);

        if (successful) {
          setCompany('');
          setEmail('');
          setFirstName('');
          setLastName('');
          setWebsite('');
          setTermsAccepted(false);
          setCountryOfResidence('');
          setPhoneNumber(null);
          setAdditionalTermsAccepted(null);
        }
      })();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      submitState,
      email,
      leadSource,
      firstName,
      lastName,
      company,
      website,
      termsAccepted,
      countryOfResidence,
      phoneNumber,
      additionalTermsAccepted
    ]
  );

  return (
    <>
      {successMessageVisible && (
        <div className="boat-pro-form__success-message">
          <p className="boat-pro-form__success-message__paragraph">
            {!successMessage && <>Thank you for completing our contact form.</>}
            {successMessage && successMessage}
          </p>

          <p className="boat-pro-form__success-message__paragraph">
            We will be in contact with you shortly.
          </p>
        </div>
      )}

      {!successMessageVisible && (
        <>
          {submitState && <div className="boat-pro-form__overlay" />}

          <div className={fieldsWrapperClass}>
            <div className={doubleInputWrapperClass}>
              <div className={inputWrapperClass}>
                <input
                  className={firstNameInputClass}
                  type="text"
                  value={firstName}
                  onChange={event => setFirstName(event.target.value)}
                  onKeyDown={event => {
                    setFirstNameError(null);
                    handleKeyDown(event);
                  }}
                  placeholder="* First Name"
                />

                {firstNameError && <p className={errorClass}>{firstNameError}</p>}
              </div>

              <div className={inputWrapperClass}>
                <input
                  className={lastNameInputClass}
                  type="text"
                  value={lastName}
                  onChange={event => setLastName(event.target.value)}
                  onKeyDown={event => {
                    setLastNameError(null);
                    handleKeyDown(event);
                  }}
                  placeholder="* Last Name"
                />

                {lastNameError && <p className={errorClass}>{lastNameError}</p>}
              </div>
            </div>

            {!embed && (
              <div className={doubleInputWrapperClass}>
                <div className={inputWrapperClass}>
                  <input
                    className={inputClass}
                    type="text"
                    value={phoneNumber || ''}
                    onChange={event => setPhoneNumber(event.target.value)}
                    onKeyDown={event => handleKeyDown(event)}
                    placeholder="Phone Number"
                  />
                </div>

                <div className={inputWrapperClass}>
                  <input
                    className={emailInputClass}
                    type="text"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    onKeyDown={event => {
                      setEmailError(null);
                      handleKeyDown(event);
                    }}
                    placeholder="* Email"
                  />

                  {emailError && <p className={errorClass}>{emailError}</p>}
                </div>
              </div>
            )}

            {embed && (
              <>
                <div className={inputWrapperClass}>
                  <input
                    className={inputClass}
                    type="text"
                    value={phoneNumber || ''}
                    onChange={event => setPhoneNumber(event.target.value)}
                    onKeyDown={event => handleKeyDown(event)}
                    placeholder="Phone Number"
                  />
                </div>

                <div className={inputWrapperClass}>
                  <input
                    className={emailInputClass}
                    type="text"
                    value={email}
                    onChange={event => setEmail(event.target.value)}
                    onKeyDown={event => {
                      setEmailError(null);
                      handleKeyDown(event);
                    }}
                    placeholder="* Email"
                  />

                  {emailError && <p className={errorClass}>{emailError}</p>}
                </div>
              </>
            )}

            <div className={inputWrapperClass}>
              <input
                className={companyInputClass}
                type="text"
                value={company}
                onChange={event => setCompany(event.target.value)}
                onKeyDown={event => {
                  setCompanyError(null);
                  handleKeyDown(event);
                }}
                placeholder="* Company"
              />

              {companyError && <p className={errorClass}>{companyError}</p>}
            </div>

            <div className={inputWrapperClass}>
              <input
                className={websiteInputClass}
                type="text"
                value={website}
                onChange={event => setWebsite(event.target.value)}
                onKeyDown={event => {
                  setWebsiteError(null);
                  handleKeyDown(event);
                }}
                placeholder="* Website"
              />

              {websiteError && <p className={errorClass}>{websiteError}</p>}
            </div>

            <div className={inputWrapperClass}>
              <Select
                name="countryOfResidence"
                className={countryOfResidenceInputClass}
                options={countryOfResidenceOptions}
                onChange={({ value }) => {
                  setCountryOfResidenceError(null);
                  setCountryOfResidence(value);
                }}
                selected={countryOfResidence}
              />

              {countryOfResidenceError && <p className={errorClass}>{countryOfResidenceError}</p>}
            </div>

            <div className={termsAcceptedInputClass}>
              <input
                readOnly
                className="boat-pro-form__checkbox__box"
                type="checkbox"
                checked={termsAccepted}
                name="termsAccepted"
                onClick={() => {
                  setTermsAcceptedError(null);
                  setTermsAccepted(!termsAccepted);
                }}
              />

              <div
                className="boat-pro-form__checkbox__box-label"
                onClick={() => {
                  setTermsAcceptedError(null);
                  setTermsAccepted(!termsAccepted);
                }}
              />

              <label
                className="boat-pro-form__checkbox__label"
                onClick={() => {
                  setTermsAcceptedError(null);
                  setTermsAccepted(!termsAccepted);
                }}>
                I agree to the{' '}
                <a
                  href={PRIVACY.TERMS_HREF}
                  className="boat-pro-form__checkbox__link"
                  target="_blank"
                  rel="noreferrer">
                  terms and conditions
                </a>{' '}
                and{' '}
                <a
                  href={PRIVACY.HREF}
                  className="boat-pro-form__checkbox__link"
                  target="_blank"
                  rel="noreferrer">
                  privacy notice
                </a>
                .
              </label>
            </div>

            <div className={additionalTermsAcceptedInputClass}>
              <label className="boat-pro-form__radio__label">
                I would like to receive tailored information about events and products from BOAT
                International Media Ltd, an Informa Group Company. You can opt out at any time by
                clicking unsubscribe or contacting us. For Further details, see our{' '}
                <a
                  href={PRIVACY.HREF}
                  className="boat-pro-form__checkbox__footer-link"
                  target="_blank"
                  rel="noreferrer">
                  privacy notice
                </a>
                .
              </label>

              <div className="boat-pro-form__radio__boxes">
                <input
                  readOnly
                  type="radio"
                  name="additionalTermsAccepted"
                  value="yes"
                  className="boat-pro-form__radio__box"
                  checked={additionalTermsAccepted === true}
                  onClick={() => setAdditionalTermsAccepted(true)}
                />
                <label
                  className="boat-pro-form__radio__box-label"
                  onClick={() => setAdditionalTermsAccepted(true)}>
                  Yes
                </label>{' '}
                <input
                  readOnly
                  type="radio"
                  name="additionalTermsAccepted"
                  value="no"
                  className="boat-pro-form__radio__box"
                  checked={additionalTermsAccepted === false}
                  onClick={() => setAdditionalTermsAccepted(false)}
                />
                <label
                  className="boat-pro-form__radio__box-label"
                  onClick={() => setAdditionalTermsAccepted(false)}>
                  No
                </label>
              </div>
            </div>

            <a className="boat-pro-form__submit" onClick={() => setSubmitState({})}>
              Submit
            </a>
          </div>
        </>
      )}
    </>
  );
};

BoatProForm.propTypes = {
  embed: PropTypes.bool.isRequired,
  leadSource: PropTypes.string.isRequired,
  successMessage: PropTypes.string
};

BoatProForm.defaultProps = {
  successMessage: ''
};

export default BoatProForm;
